import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { v4 as uuidv4 } from "uuid"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import axios from "axios"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { toast } from "@/components/ui/use-toast"
import QRCodeStyling from "qr-code-styling"

const formSchema = z.object({
  iranyitoszam: z
    .string()
    .length(4, { message: "Az irányítószámnak 4 számjegyűnek kell lennie" }),
  telepules: z.string().min(1, { message: "A település megadása kötelező" }),
  utcaHazszam: z
    .string()
    .min(1, { message: "Az utca és házszám megadása kötelező" }),
  email: z
    .string()
    .email({ message: "Érvénytelen e-mail cím" })
    .optional()
    .or(z.literal("")),
  telefonszam: z
    .string()
    .regex(
      /^(\+36|06)[ -]?([1-9][0-9]{1,2})[ -]?([0-9]{3,4})[ -]?([0-9]{3,4})$/,
      {
        message: "Érvénytelen magyar telefonszám",
      },
    )
    .optional()
    .or(z.literal("")),
  hozzajarulas: z.boolean().refine((val) => val === true, {
    message: "Az adatkezelési feltételek elfogadása kötelező",
  }),
})

function BillingForm() {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      iranyitoszam: "",
      telepules: "",
      utcaHazszam: "",
      email: "",
      telefonszam: "",
      hozzajarulas: false,
    },
  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true)
    try {
      const response = await axios.post("http://localhost:4000", values)
      toast({
        title: "Sikeres küldés",
        description: "Az űrlap sikeresen elküldve.",
      })
      console.log(response.data)
    } catch (error) {
      toast({
        title: "Hiba történt",
        description: "Az űrlap küldése sikertelen.",
        variant: "destructive",
      })
      console.error("Submission error:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const isFormValid =
    form.formState.isValid && Object.keys(form.formState.errors).length === 0

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">Számlázási Cím</h2>
          <div className="flex gap-4">
            <FormField
              control={form.control}
              name="iranyitoszam"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Irányítószám</FormLabel>
                  <FormControl>
                    <Input placeholder="1234" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="telepules"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Település</FormLabel>
                  <FormControl>
                    <Input placeholder="Budapest" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="utcaHazszam"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Utca, Házszám</FormLabel>
                <FormControl>
                  <Input placeholder="Példa utca 1." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>E-mail (opcionális)</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="pelda@email.com"
                    {...field}
                  />
                </FormControl>
                <FormDescription></FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="telefonszam"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Telefonszám (opcionális)</FormLabel>
                <FormControl>
                  <Input placeholder="+36 30 123 4567" {...field} />
                </FormControl>
                <FormDescription></FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="hozzajarulas"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    Hozzájárulok az adatkezelési feltételekhez
                  </FormLabel>
                </div>
              </FormItem>
            )}
          />
        </div>
        <Button type="submit" /*disabled={!isFormValid || isSubmitting}*/>
          {isSubmitting ? "Küldés..." : "Jelentkezés"}
        </Button>
      </form>
    </Form>
  )
}

const generateQRCode = () => {
  const code = uuidv4()
  console.log(code)
  return new QRCodeStyling({
    width: 300,
    height: 300,
    type: "svg",
    data: "https://beart.hu/utalvany/check?code=" + code,
    image: "http://localhost:3000/images/beart-logo-qr.svg",
    dotsOptions: {
      color: "#000000",
      type: "square",
    },
    backgroundOptions: {
      color: "#ffffff", // 033c38
    },
    imageOptions: {
      crossOrigin: "anonymous",
      margin: 2,
    },
  })
}

export default function Utalvany() {
  return (
    <>
      <Helmet>
        <title>Utalvány | BeArt Művészetterápia</title>
        <meta name="description" content="Utalvány vásárlás" />
        <link rel="canonical" href="https://beart.hu/utalvany" />
      </Helmet>
      
      <div className="container mx-auto w-2/3 p-4">
        <div className="mt-10 border-2 border-amber-900 p-4">
          <BillingForm />
        </div>
        <div className="absolute">
          <img src={`/images/ajandekkartya.png`} alt="Ajándékkártya" />
          <div
            id="canvas"
            className="h-[300px] w-[300px] bg-gray-400"
          ></div>
        </div>
      </div>
    </>
  )
}
